import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Gift, X, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useSpinPrizes } from "../lib/hooks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SPIN_KEY = "stealodeal_spin_v1";

export default function SpinWheel() {
  const prizes = useSpinPrizes();
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null); // { label, coupon_code, color }
  const [rotation, setRotation] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const wheelRef = useRef(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SPIN_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.result) setResult(parsed.result);
      }
    } catch { /* ignore */ }
  }, []);

  const spin = async () => {
    if (spinning || prizes.length === 0) return;
    setSpinning(true);
    try {
      const { data } = await axios.post(`${API}/spin/play`);
      // find matching segment index for animation
      const idx = prizes.findIndex((p) => p.label === data.label);
      const seg = 360 / prizes.length;
      const target = idx >= 0 ? idx : 0;
      // 6 full spins + land on segment center
      const finalRotation = 360 * 6 + (360 - target * seg - seg / 2);
      setRotation(finalRotation);
      setTimeout(() => {
        setResult(data);
        setSpinning(false);
        try { localStorage.setItem(SPIN_KEY, JSON.stringify({ result: data, at: Date.now() })); } catch { /* ignore */ }
      }, 4500);
    } catch (e) {
      toast.error("Something went wrong", { description: e?.response?.data?.detail || e.message });
      setSpinning(false);
    }
  };

  const copyCode = () => {
    if (!result?.coupon_code) return;
    navigator.clipboard.writeText(result.coupon_code);
    toast.success(`Code ${result.coupon_code} copied — apply at checkout`);
  };

  const closeAndDismiss = () => {
    setOpen(false);
    setDismissed(true);
  };

  if (prizes.length === 0) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && !dismissed && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 left-6 z-40 h-14 pl-4 pr-6 rounded-full bg-[#FF5A1F] hover:bg-neutral-900 text-white font-bold text-sm shadow-2xl flex items-center gap-2 transition-all animate-bounce-slow"
          data-testid="spin-open-btn"
        >
          <Gift className="w-5 h-5" />
          <span>Spin & Win</span>
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeAndDismiss} />
          <div className="relative bg-white rounded-3xl w-full max-w-md p-6 md:p-8 text-center" data-testid="spin-modal">
            <button onClick={closeAndDismiss} className="absolute top-4 right-4 p-2 text-neutral-500 hover:text-neutral-900" data-testid="spin-close-btn">
              <X className="w-5 h-5" />
            </button>

            {!result ? (
              <>
                <div className="flex items-center justify-center gap-2 text-[#FF5A1F] text-xs font-bold uppercase tracking-[0.26em]">
                  <Sparkles className="w-3.5 h-3.5" /> Exclusive Reward
                </div>
                <h2 className="mt-2 font-display text-3xl md:text-4xl font-black">Spin & Win!</h2>
                <p className="mt-2 text-sm text-neutral-500">Get up to 15% OFF, free shipping or a free keychain.</p>

                <div className="relative mx-auto my-8 w-72 h-72">
                  {/* Pointer */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 z-20">
                    <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[18px] border-l-transparent border-r-transparent border-t-neutral-900" />
                  </div>
                  {/* Wheel */}
                  <div
                    ref={wheelRef}
                    className="w-full h-full rounded-full border-[6px] border-neutral-900 overflow-hidden relative shadow-xl"
                    style={{
                      transform: `rotate(${rotation}deg)`,
                      transition: spinning ? "transform 4.4s cubic-bezier(.17,.67,.25,1)" : "none",
                    }}
                  >
                    {prizes.map((p, i) => {
                      const seg = 360 / prizes.length;
                      const rot = i * seg;
                      return (
                        <div
                          key={i}
                          className="absolute inset-0"
                          style={{
                            clipPath: `polygon(50% 50%, ${50 + 50 * Math.cos((rot - seg / 2) * Math.PI / 180)}% ${50 + 50 * Math.sin((rot - seg / 2) * Math.PI / 180)}%, ${50 + 50 * Math.cos((rot + seg / 2) * Math.PI / 180)}% ${50 + 50 * Math.sin((rot + seg / 2) * Math.PI / 180)}%)`,
                            background: p.color || (i % 2 === 0 ? "#FF5A1F" : "#171717"),
                          }}
                        >
                          <div
                            className="absolute left-1/2 top-1/2 text-white font-bold text-[11px] whitespace-nowrap"
                            style={{
                              transform: `translate(-50%, -50%) rotate(${rot}deg) translateY(-95px) rotate(90deg)`,
                              transformOrigin: "center",
                            }}
                          >
                            {p.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Center hub */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-12 h-12 rounded-full bg-white border-4 border-neutral-900 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-neutral-900" />
                    </div>
                  </div>
                </div>

                <button
                  onClick={spin}
                  disabled={spinning}
                  className="w-full h-12 rounded-full bg-[#FF5A1F] hover:bg-neutral-900 disabled:opacity-60 text-white font-bold text-sm transition"
                  data-testid="spin-play-btn"
                >
                  {spinning ? "Spinning..." : "SPIN NOW"}
                </button>
                <p className="mt-3 text-[10px] text-neutral-400 uppercase tracking-widest">One free spin · No purchase required</p>
              </>
            ) : (
              <div data-testid="spin-result">
                <div className="text-5xl">🎉</div>
                <h2 className="mt-3 font-display text-3xl font-black">You won!</h2>
                <div className="mt-2 text-2xl font-black text-[#FF5A1F]">{result.label}</div>
                {result.coupon_code && (
                  <>
                    <p className="mt-4 text-xs text-neutral-500 uppercase tracking-widest">Your coupon code</p>
                    <div className="mt-2 mx-auto flex items-center justify-between gap-2 bg-neutral-100 rounded-2xl px-5 py-3 max-w-xs">
                      <code className="font-mono font-bold text-lg">{result.coupon_code}</code>
                      <button onClick={copyCode} className="text-[#FF5A1F] hover:text-neutral-900" data-testid="copy-code-btn">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="mt-3 text-xs text-neutral-500">Apply this code at checkout</p>
                  </>
                )}
                <button
                  onClick={closeAndDismiss}
                  className="mt-6 w-full h-11 rounded-full bg-neutral-900 hover:bg-[#FF5A1F] text-white font-bold text-sm transition"
                  data-testid="spin-done-btn"
                >
                  Continue Shopping
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .animate-bounce-slow { animation: bounce-slow 2.4s ease-in-out infinite; }
      `}</style>
    </>
  );
}
